import {
  DefaultDataServiceConfig,
  EntityDataModuleConfig,
  EntityMetadata,
  EntityMetadataMap,
} from '@ngrx/data';
import { Dictionary } from '@ngrx/entity';
import {
  NotificationSettingInterface,
  NotificationTelegramChannelInterface,
  TagInterface,
} from '@shared/api';
import { environment } from '@shared/environments';

export interface EntityInterface {
  guid: string;
  id: string;
  owner?: {
    guid: string;
    name: string;
  };
  name?: string;
  isPrivate?: boolean;
  tags?: TagInterface[];
}

export enum EntityTypeEnum {
  Site = 'Site',
  Node = 'Node',
  Client = 'Client',
  CliCommand = 'CliCommand',
  CliTemplate = 'CliTemplate',
  SelectSite = 'SelectSite',
  Task = 'Task',
  // TODO: Remove when the new role model is released
  TeamMemberOld = 'TeamMemberOld',
  Team = 'Team',
  TeamMember = 'TeamMember',
  TeamSite = 'TeamSite',
  Event = 'Event',
  Tag = 'Tag',
  NotificationSetting = 'NotificationSetting',
  TelegramChannel = 'TelegramChannel',
}

export interface EntityModelInterface<T extends EntityInterface> {
  items: T[];
  itemsMap: Dictionary<T>;
  itemsCount: number;
  totalItemsCount: number;
  pinnedItems?: T[];
}

const defaultEntityMetadata: Partial<EntityMetadata<EntityInterface>> = {
  selectId: (entity) => entity.guid,
  additionalCollectionState: {
    itemsCount: 0,
    totalItemsCount: undefined,
    pinnedItems: undefined,
  },
};

export const entityMetadata: EntityMetadataMap = {
  [EntityTypeEnum.Site]: defaultEntityMetadata,
  [EntityTypeEnum.Node]: defaultEntityMetadata,
  [EntityTypeEnum.Client]: defaultEntityMetadata,
  [EntityTypeEnum.CliCommand]: defaultEntityMetadata,
  [EntityTypeEnum.CliTemplate]: defaultEntityMetadata,
  [EntityTypeEnum.SelectSite]: defaultEntityMetadata,
  [EntityTypeEnum.Task]: defaultEntityMetadata,
  [EntityTypeEnum.Team]: defaultEntityMetadata,
  [EntityTypeEnum.TeamMemberOld]: defaultEntityMetadata,
  [EntityTypeEnum.TeamMember]: defaultEntityMetadata,
  [EntityTypeEnum.TeamSite]: defaultEntityMetadata,
  [EntityTypeEnum.Event]: defaultEntityMetadata,
  [EntityTypeEnum.Tag]: defaultEntityMetadata,
  [EntityTypeEnum.NotificationSetting]: {
    selectId: (entity: NotificationSettingInterface) =>
      entity.id ?? entity.guid,
  },
  [EntityTypeEnum.TelegramChannel]: {
    selectId: (entity: NotificationTelegramChannelInterface) => entity.guid,
  },
};

const pluralNames = {
  [EntityTypeEnum.Site]: 'Sites',
  [EntityTypeEnum.Node]: 'Nodes',
  [EntityTypeEnum.Client]: 'Clients',
  [EntityTypeEnum.CliCommand]: 'CliCommands',
  [EntityTypeEnum.CliTemplate]: 'CliTemplates',
  [EntityTypeEnum.Task]: 'Tasks',
  [EntityTypeEnum.TeamMemberOld]: 'TeamMembers',
  [EntityTypeEnum.Team]: 'Teams',
  [EntityTypeEnum.TeamMember]: 'TeamMembers',
  [EntityTypeEnum.TeamSite]: 'TeamSites',
  [EntityTypeEnum.Event]: 'Events',
  [EntityTypeEnum.Tag]: 'Tags',
  [EntityTypeEnum.NotificationSetting]: 'NotificationSettings',
  [EntityTypeEnum.TelegramChannel]: 'TelegramChannels',
};

export const entityConfig: EntityDataModuleConfig = {
  entityMetadata,
  pluralNames,
};

export const defaultDataServiceConfig: DefaultDataServiceConfig = {
  timeout: 60000,
  entityHttpResourceUrls: {
    [EntityTypeEnum.Site]: {
      entityResourceUrl: `${environment.apiHost}/sites/`,
      collectionResourceUrl: `${environment.apiHost}/sites`,
    },
    [EntityTypeEnum.Node]: {
      entityResourceUrl: `${environment.apiHost}/nodes/`,
      collectionResourceUrl: `${environment.apiHost}/nodes`,
    },
    [EntityTypeEnum.Client]: {
      entityResourceUrl: `${environment.apiHost}/clients/`,
      collectionResourceUrl: `${environment.apiHost}/clients`,
    },
    [EntityTypeEnum.CliCommand]: {
      entityResourceUrl: `${environment.apiHost}/cli/command/`,
      collectionResourceUrl: `${environment.apiHost}/cli/command`,
    },
    [EntityTypeEnum.CliTemplate]: {
      entityResourceUrl: `${environment.apiHost}/cli/template/`,
      collectionResourceUrl: `${environment.apiHost}/cli/template`,
    },
    [EntityTypeEnum.Task]: {
      entityResourceUrl: `${environment.apiHost}/tasks/`,
      collectionResourceUrl: `${environment.apiHost}/tasks`,
    },
    // TODO: Remove when the new role model is released
    [EntityTypeEnum.TeamMemberOld]: {
      entityResourceUrl: `${environment.apiHost}/team/participants/`,
      collectionResourceUrl: `${environment.apiHost}/team/participants`,
    },
    [EntityTypeEnum.Team]: {
      entityResourceUrl: `${environment.apiHost}/team_v2/`,
      collectionResourceUrl: `${environment.apiHost}/team_v2`,
    },
    [EntityTypeEnum.TeamMember]: {
      entityResourceUrl: `${environment.apiHost}/team_v2/:id/members/`,
      collectionResourceUrl: `${environment.apiHost}/team_v2/:id/members`,
    },
    [EntityTypeEnum.TeamSite]: {
      entityResourceUrl: `${environment.apiHost}/team_v2/:id/sites/`,
      collectionResourceUrl: `${environment.apiHost}/team_v2/:id/sites`,
    },
    [EntityTypeEnum.SelectSite]: {
      entityResourceUrl: `${environment.apiHost}/sites/search/`,
      collectionResourceUrl: `${environment.apiHost}/sites/search`,
    },
    [EntityTypeEnum.Event]: {
      entityResourceUrl: `${environment.apiHost}/event_logs/`,
      collectionResourceUrl: `${environment.apiHost}/event_logs`,
    },
    [EntityTypeEnum.Tag]: {
      entityResourceUrl: `${environment.apiHost}/tags/`,
      collectionResourceUrl: `${environment.apiHost}/tags`,
    },
    [EntityTypeEnum.NotificationSetting]: {
      entityResourceUrl: `${environment.apiHost}/notification/settings/`,
      collectionResourceUrl: `${environment.apiHost}/notification/settings`,
    },
    [EntityTypeEnum.TelegramChannel]: {
      entityResourceUrl: `${environment.apiHost}/notification/channel/telegram/`,
      collectionResourceUrl: `${environment.apiHost}/notification/channel/telegram`,
    },
  },
};
